import { useQuery } from "@tanstack/react-query";

import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import { useClaim } from "../api/use-claim";
import { FamilyAcquisitionCard } from "../components/acquisition/family-acquisition-card";
import { FamilyAcquisitionCardSkeleton } from "../components/acquisition/family-acquisition-card-skeleton";
import { AgentFeedbackFlow } from "../components/agent-feedback/agent-feedback-flow";

export interface AcquisitionHistoryTabProps {
  claimId: string;
  variant?: "full" | "compact";
}

/** Port of pages/plugin/AcquisitionHistory.vue. */
export function AcquisitionHistoryTab({ claimId }: AcquisitionHistoryTabProps) {
  const { data: claim } = useClaim(claimId);

  const { data: claimAcquisitions, isPending } = useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "acquisitions"],
    queryFn: () => adminAPI.listClaimAcquisitions(claimId),
  });

  if (!claim) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const hasAutomatedFlow = isPending || Boolean(claimAcquisitions);

  return (
    <div>
      <Tabs
        key={hasAutomatedFlow ? "automated" : "manual"}
        defaultValue={hasAutomatedFlow ? "automated" : "manual"}
      >
        <TabsList className="mt-2">
          {hasAutomatedFlow ? <TabsTrigger value="automated">Flow automatisé</TabsTrigger> : null}
          <TabsTrigger value="manual">Manuel</TabsTrigger>
        </TabsList>
        {hasAutomatedFlow ? (
          <TabsContent value="automated">
            {isPending ? (
              <div className="h-full bg-slate-50 p-4">
                <FamilyAcquisitionCardSkeleton />
              </div>
            ) : (
              <div className="h-full bg-slate-50 p-4">
                {claimAcquisitions?.enterprise ? (
                  <div className="my-8 rounded-md border bg-white p-6">
                    {JSON.stringify(claimAcquisitions.enterprise, null, 2)}
                  </div>
                ) : null}
                {claimAcquisitions?.families.map((acquisition) => (
                  <FamilyAcquisitionCard key={acquisition.id} acquisition={acquisition} claimId={claim.id} />
                ))}
              </div>
            )}
          </TabsContent>
        ) : null}
        <TabsContent value="manual">
          <AgentFeedbackFlow claim={claim} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
